import React, { useEffect, useState } from 'react';
import LogoLang from '../components/LogoLang';
import customFetch from '../utils/customFetch';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const Linkvalidation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const userObject = localStorage.getItem('user');
  const user = userObject != '' ? JSON.parse(userObject) : '';
  const [validationResult, setValidationResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claimingCard, setClaimingCard] = useState(false);

  useEffect(() => {
    fetchValidationData();
  }, [id]);

  const fetchValidationData = async () => {
    try {
      await delay(2000); // 2 seconds delay
      const { data } = await customFetch.get(`/public/linkValidation/${id}`);

      if (data.status === 'active') {
        return navigate(`/c/${data.ownerUrl}`);
      }

      setValidationResult(data);
    } catch (error) {
      await delay(2000); // 2 seconds delay
      navigate('/404');
    } finally {
      setLoading(false);
    }
  };

  const handleClaimCard = async () => {
    setClaimingCard(true);
    try {
      await customFetch.post(`/cards/${id}/claim`);
      // Nach erfolgreichem Claim zum Profil weiterleiten
      toast.success(
        'Die Karte wurde erfolgreich zu deinem Profil hinzugefügt!'
      );
      navigate('/dashboard');
    } catch (error) {
      console.error('Fehler beim Beanspruchen der Karte:', error);
      toast.error(error?.response?.data?.msg);
      // Hier könntest du eine Toast-Nachricht oder Error-State setzen
    } finally {
      setClaimingCard(false);
    }
  };

  const getStatusContent = () => {
    const { status } = validationResult;

    const statusConfig = {
      ordered: {
        title: 'Diese Seite ist noch nicht aktiv',
        description:
          'Warte noch, bis deine Bestellung abgeschlossen ist und deine Karte mit deinem Profil verknüpft wurde. Das dauert in der Regel nicht lange.',
      },
      blocked: {
        title: 'Dieser Link wurde blockiert',
        description:
          'Falls das nicht so sein sollte, wende dich bitte an unseren Support.',
      },
      unclaimed: {
        title: 'Du kannst diese Karte beanspruchen',
        description:
          'Diese Karte ist verfügbar und kann mit deinem Profil verknüpft werden.',
      },
      default: {
        title: 'Hier ist ein Fehler aufgetreten',
        description:
          'Falls das nicht so sein sollte, wende dich bitte an unseren Support unter support@svisit.ch.',
      },
    };

    return statusConfig[status] || statusConfig.default;
  };

  const renderUnclaimedVariants = () => {
    if (user) {
      // Variante 1: Eingeloggt - Usercard mit Verknüpfung
      return (
        <div className="mt-10 max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6 border">
            <div className="flex items-center space-x-4 mb-6">
              <img
                src={
                  user.avatar ||
                  'https://res.cloudinary.com/djvqy2bag/image/upload/v1729680126/Default_user_pic_ncdhdy.jpg'
                }
                alt={user.name}
                className="w-16 h-16 rounded-full object-cover"
              />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {user.name} {user.lastName}
                </h3>
                <p className="text-sm text-gray-600">{user.email}</p>
              </div>
            </div>
            <button
              onClick={handleClaimCard}
              disabled={claimingCard}
              className="w-full hover:cursor-pointer bg-svisit-primary-600 hover:bg-svisit-primary-700 disabled:bg-svisit-primary-400 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
            >
              {claimingCard ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Verknüpfung läuft...
                </span>
              ) : (
                'Mit diesem Profil verknüpfen'
              )}
            </button>
          </div>
        </div>
      );
    } else {
      // Variante 2: Nicht eingeloggt - Login/Register Cards
      return (
        <div className="mt-10 max-w-2xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Login Card */}
            <div className="bg-white rounded-lg shadow-lg p-8 border hover:shadow-xl transition-shadow duration-300">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-svisit-primary-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Bereits registriert?
                </h3>
                <p className="text-gray-600 mb-6">
                  Logge dich ein und verknüpfe die Karte mit deinem bestehenden
                  Profil.
                </p>
                <Link
                  to={`/login?redirectURL=/l/${id}`}
                  className="inline-flex items-center justify-center w-full bg-svisit-primary-600 hover:bg-svisit-primary-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
                >
                  Login und verknüpfen
                </Link>
              </div>
            </div>

            {/* Register Card */}
            <div className="bg-white rounded-lg shadow-lg p-8 border hover:shadow-xl transition-shadow duration-300">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-svisit-primary-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Neu bei sVisit?
                </h3>
                <p className="text-gray-600 mb-6">
                  Erstelle ein neues Profil und verknüpfe die Karte direkt
                  damit.
                </p>
                <Link
                  to={'/register'}
                  className="inline-flex items-center justify-center w-full bg-svisit-primary-600 hover:bg-svisit-primary-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
                >
                  Registrieren und verknüpfen
                </Link>
              </div>
            </div>
          </div>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="grid min-h-screen place-content-center">
        <div className="animate-pulse-scale">
          <LogoLang />
        </div>
      </div>
    );
  }

  if (!validationResult) return null;

  const { title, description } = getStatusContent();

  return (
    <div className="grid min-h-screen place-content-center px-6 lg:px-8">
      <div>
        <LogoLang />
      </div>

      <h1 className="mt-10 text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl text-center">
        {title}
      </h1>

      <p className="mt-4 text-base text-gray-600 sm:mt-6 text-center w-full">
        {description}
      </p>

      {/* Spezielle Behandlung für unclaimed Status */}
      {validationResult.status === 'unclaimed' && renderUnclaimedVariants()}

      <div className="mt-10 flex justify-center">
        <Link
          to="/"
          className="text-sm font-semibold leading-7 text-gray-900 hover:text-gray-700 transition-colors"
        >
          <span aria-hidden="true">&larr;</span> Zur Homepage
        </Link>
      </div>
    </div>
  );
};

export default Linkvalidation;
